import { useEffect, useState } from 'react';
import { catchError, EMPTY, tap } from 'rxjs';

import useSecuredApiV2 from './use-secured-api-v2';
import ProblemDetailsDto from '../dto/error/problem-details-dto';
import { type ResponseDto } from '../dto/ResponseDto';

interface PaginatedListState<T> {
  data: T[];
  totalRecords: number;
  isLoading: boolean;
  errorMessage?: string;
}

export interface PaginatedListResult<T> extends PaginatedListState<T> {
  refetch: () => void;
}

const toErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof ProblemDetailsDto) return error.detail;
  if (error instanceof Error) return error.message;
  return undefined;
};

/**
 * Loads a page of "url" with "params", mapping each returned row with "toItem".
 * It loads again whenever the content of "params" changes or "refetch" is called. A failed load clears the list and
 * sets "errorMessage" (falling back to "defaultErrorMessage").
 */
const usePaginatedList = <Dto, T>(
  url: string,
  params: object,
  toItem: (dto: Dto) => T,
  defaultErrorMessage: string
): PaginatedListResult<T> => {
  const api = useSecuredApiV2();
  const [state, setState] = useState<PaginatedListState<T>>({
    data: [],
    totalRecords: 0,
    isLoading: true,
  });
  // Changing it re-runs the effect below, which loads the list again with the same params
  const [reloadCount, setReloadCount] = useState(0);
  // Params are rebuilt on every render, so compare them by value
  const paramsKey = JSON.stringify(params);

  useEffect(() => {
    setState((previous) => ({ ...previous, isLoading: true, errorMessage: undefined }));

    const subscription = api
      .get(url, params, {})
      .pipe(
        tap((response: ResponseDto<Dto[]>) => {
          setState({
            data: response.data.map(toItem),
            totalRecords: response.pagination?.totalRecords ?? 0,
            isLoading: false,
          });
        }),
        catchError((error: unknown) => {
          // Clear the list, so rows loaded for previous params aren't shown as results of the current ones
          setState({
            data: [],
            totalRecords: 0,
            isLoading: false,
            errorMessage: toErrorMessage(error) ?? defaultErrorMessage,
          });
          return EMPTY;
        })
      )
      .subscribe();

    // Ignore the response if params change, refetch is called or the component unmounts before it arrives
    return () => {
      subscription.unsubscribe();
    };
  }, [url, paramsKey, reloadCount]);

  const refetch = (): void => {
    setReloadCount((count) => count + 1);
  };

  return { ...state, refetch };
};

export default usePaginatedList;
